export const a = 1;

import { MoneySchema } from "@redotech/money";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  ProductVariant,
  Customer as ShopifyCustomer,
} from "../../common/shopify-pixel-types";
import {
  CheckoutConversionMetricInformationStatus,
  CheckoutConversionMetricSource,
} from "../../conversion/checkout-conversion-metrics/checkout-conversion-metrics";
import { Provider } from "../../order";
import {
  EventProducer,
  IdentityResolver,
} from "../shopify-storefront-event-payload";
import {
  baseCustomerEventSchema,
  CustomerEventType,
  PaymentFailureStage,
} from "./base";
import {
  redoCartLineSchema,
  redoCheckoutInfoSchema,
  redoStorefrontEventProductSchema,
  shopifyCartLineSchema,
  shopifyCartLineToRedoCartLine,
  shopifyCheckoutSchema,
  shopifyCheckoutToRedoCheckout,
  shopifyPixelEventCartSchema,
  shopifyProductVariantToRedoProductVariant,
} from "./storefront-event-primitives";

type PartialShopifyCustomer = Partial<ShopifyCustomer> | null | undefined;

export const LocalDayOfWeek = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;
export type LocalDayOfWeek = (typeof LocalDayOfWeek)[number];

export const baseShoppingEventSchema = baseCustomerEventSchema.extend({
  externalEventId: z.string(),
  externalCustomer: z
    .any()
    .optional()
    .transform((val: any) => val as PartialShopifyCustomer),
  sessionId: z.string().optional(),
  ipAddress: z.string().optional(),
  storefrontProvider: createEnum(Provider, { enumName: "Provider" }),
  rawEvent: z.any().nullish(),
  pricingTestTreatmentId: z.string().optional(),
  /** How the email was resolved. Pixel-only; non-shopping events don't carry this. */
  emailIdentityResolver: createEnum(IdentityResolver, {
    enumName: "IdentityResolver",
  }).optional(),
  /** How the phone was resolved. */
  phoneIdentityResolver: createEnum(IdentityResolver, {
    enumName: "IdentityResolver",
  }).optional(),
  /** Which system provided the email on this event. Matches producer for
   *  single-source events; differs when the checkout buffer merges redo
   *  base + contextual identity enrichment. */
  emailIdentityProducer: createEnum(EventProducer, {
    enumName: "EventProducer",
  }).optional(),
  /** Which system provided the phone on this event. */
  phoneIdentityProducer: createEnum(EventProducer, {
    enumName: "EventProducer",
  }).optional(),
});
export type BaseShoppingEvent = z.infer<typeof baseShoppingEventSchema>;

export const StorePageViewedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.STORE_PAGE_VIEWED),
    urlWithoutParams: zExt.coerceNull(z.string()),
    urlWithParams: zExt.coerceNull(z.string()),
  });
export const StorePageViewedShoppingEventSchema: z.ZodType<StorePageViewedShoppingEvent> =
  StorePageViewedShoppingEventSchema_;
export interface StorePageViewedShoppingEvent
  extends z.infer<typeof StorePageViewedShoppingEventSchema_> {}

//////////////////////////////////////
/////////// PRODUCT VIEWED ///////////
//////////////////////////////////////
const baseProductViewedShoppingEventSchema = baseShoppingEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRODUCT_VIEWED),
  viewedProduct: zExt.coerceNull(redoStorefrontEventProductSchema),
});

const shopifyProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z
      .literal(Provider.SHOPIFY)
      .nullish()
      .transform((val) => val ?? Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `viewedProduct` instead. */
    productVariant: z
      .any()
      .transform((val: any) => val as ProductVariant)
      .nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: z.object({ id: z.string().nullish() }).nullish(), // intentionally stripped down since cart data is less useful for this event
  });
const commentsoldProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
export const ProductViewedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyProductViewedShoppingEventSchema,
    commentsoldProductViewedShoppingEventSchema,
  ],
);
export const ProductViewedShoppingEventSchema: z.ZodType<ProductViewedShoppingEvent> =
  ProductViewedShoppingEventSchema_;
export type ProductViewedShoppingEvent = z.infer<
  typeof ProductViewedShoppingEventSchema_
>;

/**
 * Translate from legacy storefront event schema to new provider-agnostic schema.
 *
 * Historically, our internal schema for these events was very Shopify-specific.
 * Now that we're going to be ingesting from Commentsold as well, we want to have
 * a more generic schema. Unless/until we migrate all of our events (a >1 week
 * migration), we need to handle events matching either schema.
 *
 * Can't include this transformation in the zod schema because then we couldn't put
 * the resulting schema in a discriminated union.
 */
export function standardizeProductViewedShoppingEvent(
  event: ProductViewedShoppingEvent,
): ProductViewedShoppingEvent {
  if ("productVariant" in event) {
    if (!event.productVariant) {
      throw new Error("Malformed product viewed shopping event");
    }
    return {
      _id: event._id,
      team: event.team,
      email: event.email,
      customer: event.customer,
      timestamp: event.timestamp,
      idempotencyKey: event.idempotencyKey,
      isDuplicate: event.isDuplicate,
      eventType: event.eventType,
      importSource: event.importSource,
      aiAttributions: event.aiAttributions,
      eventSource: event.eventSource,
      externalEventId: event.externalEventId,
      externalCustomer: event.externalCustomer,
      sessionId: event.sessionId,
      ipAddress: event.ipAddress,
      storefrontProvider: event.storefrontProvider,
      viewedProduct: shopifyProductVariantToRedoProductVariant(
        event.productVariant,
      ),
      rawEvent: event.rawEvent ?? {
        productVariant: event.productVariant,
        cart: event.cart,
      },
    };
  }
  return event;
}

//////////////////////////////////////
///////// COLLECTION VIEWED //////////
//////////////////////////////////////
export const CollectionViewedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.COLLECTION_VIEWED),
    collection: zExt.coerceNull(
      z.object({
        id: zExt.coerceNull(z.string()),
        title: zExt.coerceNull(z.string()),
      }),
    ),
  });
export const CollectionViewedShoppingEventSchema: z.ZodType<CollectionViewedShoppingEvent> =
  CollectionViewedShoppingEventSchema_;
export interface CollectionViewedShoppingEvent
  extends z.infer<typeof CollectionViewedShoppingEventSchema_> {}

//////////////////////////////////////
/////// PRODUCT ADDED TO CART ////////
//////////////////////////////////////
const baseCartEventSchema = baseShoppingEventSchema.extend({
  eventType: z.union([
    z.literal(CustomerEventType.PRODUCT_ADDED_TO_CART),
    z.literal(CustomerEventType.PRODUCT_REMOVED_FROM_CART),
  ]),
  cartState: zExt.coerceNull(
    z.object({
      subtotal: zExt.coerceNull(
        z.object({
          amount: zExt.coerceNull(z.number()),
          currencyCode: zExt.coerceNull(z.string()),
        }),
      ),
      cartId: zExt.coerceNull(z.string()),
      lineItems: zExt.coerceNull(z.array(redoCartLineSchema)),
    }),
  ),
  cartLineChanged: zExt.coerceNull(redoCartLineSchema),
});

const baseProductAddedToCartShoppingEventSchema = baseCartEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRODUCT_ADDED_TO_CART),
});
const shopifyProductAddedToCartShoppingEventSchema =
  baseProductAddedToCartShoppingEventSchema.extend({
    storefrontProvider: z
      .literal(Provider.SHOPIFY)
      .nullish()
      .transform((val) => val ?? Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `cartLineChanged` instead. */
    cartLine: shopifyCartLineSchema.nullish(),
    /**
     * Raw data from Shopify
     * @deprecated This field is shopify-specific. Use `cartState` instead.
     */
    cart: shopifyPixelEventCartSchema.nullish(),
    /** Inferred data from us (sometimes cart is null but we can still infer total) */
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cartTotal: z
      .object({
        subtotal: z.object({
          amount: z.number(),
          currencyCode: z.string().nullish(),
        }),
      })
      .nullish(),
  });
const commentsoldProductAddedToCartShoppingEventSchema =
  baseProductAddedToCartShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
export const ProductAddedToCartShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyProductAddedToCartShoppingEventSchema,
    commentsoldProductAddedToCartShoppingEventSchema,
  ],
);
export const ProductAddedToCartShoppingEventSchema: z.ZodType<ProductAddedToCartShoppingEvent> =
  ProductAddedToCartShoppingEventSchema_;
export type ProductAddedToCartShoppingEvent = z.infer<
  typeof ProductAddedToCartShoppingEventSchema_
>;

export function standardizeProductAddedToCartShoppingEvent(
  event: ProductAddedToCartShoppingEvent,
): ProductAddedToCartShoppingEvent {
  if ("cart" in event) {
    return {
      _id: event._id,
      team: event.team,
      email: event.email,
      customer: event.customer,
      timestamp: event.timestamp,
      idempotencyKey: event.idempotencyKey,
      isDuplicate: event.isDuplicate,
      eventType: event.eventType,
      importSource: event.importSource,
      aiAttributions: event.aiAttributions,
      eventSource: event.eventSource,
      externalEventId: event.externalEventId,
      externalCustomer: event.externalCustomer,
      sessionId: event.sessionId,
      ipAddress: event.ipAddress,
      storefrontProvider: event.storefrontProvider,
      rawEvent: event.rawEvent ?? {
        cartLine: event.cartLine,
        cart: event.cart,
      },

      cartLineChanged: shopifyCartLineToRedoCartLine(event.cartLine),

      cartState: {
        cartId: event.cart?.id ?? null,
        lineItems:
          event.cart?.lines?.map(shopifyCartLineToRedoCartLine) ?? null,
        subtotal: {
          amount:
            event.cartState?.subtotal?.amount ??
            event.cart?.cost?.totalAmount?.amount ??
            null,
          currencyCode:
            event.cartState?.subtotal?.currencyCode ??
            event.cart?.cost?.totalAmount?.currencyCode ??
            null,
        },
      },
    };
  }
  return event;
}

//////////////////////////////////////
///// PRODUCT REMOVED FROM CART //////
//////////////////////////////////////
const baseProductRemovedFromCartShoppingEventSchema =
  baseCartEventSchema.extend({
    eventType: z.literal(CustomerEventType.PRODUCT_REMOVED_FROM_CART),
  });
const shopifyProductRemovedFromCartShoppingEventSchema =
  baseProductRemovedFromCartShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `cartLineChanged` instead. */
    cartLine: shopifyCartLineSchema.nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: shopifyPixelEventCartSchema.nullish(),
  });
const commentsoldProductRemovedFromCartShoppingEventSchema =
  baseProductRemovedFromCartShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });

export const ProductRemovedFromCartShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyProductRemovedFromCartShoppingEventSchema,
    commentsoldProductRemovedFromCartShoppingEventSchema,
  ],
);
export const ProductRemovedFromCartShoppingEventSchema: z.ZodType<ProductRemovedFromCartShoppingEvent> =
  ProductRemovedFromCartShoppingEventSchema_;
export type ProductRemovedFromCartShoppingEvent = z.infer<
  typeof ProductRemovedFromCartShoppingEventSchema_
>;

export function standardizeProductRemovedFromCartShoppingEvent(
  event: ProductRemovedFromCartShoppingEvent,
): ProductRemovedFromCartShoppingEvent {
  if ("cart" in event) {
    return {
      _id: event._id,
      team: event.team,
      email: event.email,
      customer: event.customer,
      timestamp: event.timestamp,
      idempotencyKey: event.idempotencyKey,
      isDuplicate: event.isDuplicate,
      eventType: event.eventType,
      importSource: event.importSource,
      aiAttributions: event.aiAttributions,
      eventSource: event.eventSource,
      externalEventId: event.externalEventId,
      externalCustomer: event.externalCustomer,
      sessionId: event.sessionId,
      ipAddress: event.ipAddress,
      storefrontProvider: event.storefrontProvider,
      rawEvent: event.rawEvent ?? {
        cartLine: event.cartLine,
        cart: event.cart,
      },

      cartLineChanged: shopifyCartLineToRedoCartLine(event.cartLine),

      cartState: {
        cartId: event.cart?.id ?? null,
        lineItems:
          event.cart?.lines?.map(shopifyCartLineToRedoCartLine) ?? null,
        subtotal: {
          amount:
            event.cartState?.subtotal?.amount ??
            event.cart?.cost?.totalAmount?.amount ??
            null,
          currencyCode:
            event.cartState?.subtotal?.currencyCode ??
            event.cart?.cost?.totalAmount?.currencyCode ??
            null,
        },
      },
    };
  }
  return event;
}

////////////////////////////////////////////
///// CHECKOUT CONTACT INFO SUBMITTED //////
////////////////////////////////////////////

export const CheckoutContactInfoSubmittedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED),
    /** @deprecated This field is Shopify pixel-specific. Use `checkoutInfo` instead. */
    checkout: shopifyCheckoutSchema.nullish(),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
  });
export const CheckoutContactInfoSubmittedShoppingEventSchema: z.ZodType<CheckoutContactInfoSubmittedShoppingEvent> =
  CheckoutContactInfoSubmittedShoppingEventSchema_;
export interface CheckoutContactInfoSubmittedShoppingEvent
  extends z.infer<typeof CheckoutContactInfoSubmittedShoppingEventSchema_> {}

/////////////////////////////
///// CHECKOUT STARTED //////
/////////////////////////////

export const baseCheckoutStartedShoppingEventSchema =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_STARTED),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
    sourceName: z.string().nullish(),
  });
const shopifyCheckoutStartedShoppingEventSchema =
  baseCheckoutStartedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `checkoutInfo` instead. */
    checkout: shopifyCheckoutSchema.nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: shopifyPixelEventCartSchema.nullish(),
    /** True when the event originated from a Shopify webhook rather than a pixel event. */
    isWebhookCheckout: z.boolean().nullish(),
  });
const commentsoldCheckoutStartedShoppingEvent =
  baseCheckoutStartedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
export const CheckoutStartedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyCheckoutStartedShoppingEventSchema,
    commentsoldCheckoutStartedShoppingEvent,
  ],
);
export const CheckoutStartedShoppingEventSchema: z.ZodType<CheckoutStartedShoppingEvent> =
  CheckoutStartedShoppingEventSchema_;
export type CheckoutStartedShoppingEvent = z.infer<
  typeof CheckoutStartedShoppingEventSchema_
>;

function parseCheckoutStartedRawEvent(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
) {
  if ("cart" in event) {
    return event.rawEvent ?? { checkout: event.checkout, cart: event.cart };
  }
  if (event.eventType === CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED) {
    return { checkout: event.checkout, cart: null };
  }
  return event.rawEvent;
}

function parseCheckoutStartedCheckoutInfo(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
) {
  if ("cart" in event) {
    return (
      event.checkoutInfo ??
      (event.checkout
        ? shopifyCheckoutToRedoCheckout({
            checkout: event.checkout,
            cartId: event.cart?.id ?? null,
          })
        : null)
    );
  }
  if (event.eventType === CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED) {
    return event.checkout
      ? shopifyCheckoutToRedoCheckout({
          checkout: event.checkout,
          cartId: null,
        })
      : null;
  }
  return event.checkoutInfo;
}

function extractCheckoutStartedBaseFields(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
) {
  return {
    _id: event._id,
    team: event.team,
    email: event.email,
    customer: event.customer,
    timestamp: event.timestamp,
    idempotencyKey: event.idempotencyKey,
    isDuplicate: event.isDuplicate,
    importSource: event.importSource,
    aiAttributions: event.aiAttributions,
    eventSource: event.eventSource,
    externalEventId: event.externalEventId,
    externalCustomer: event.externalCustomer,
    sessionId: event.sessionId,
    ipAddress: event.ipAddress,
  };
}

export function standardizeCheckoutStartedShoppingEvent(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
): CheckoutStartedShoppingEvent {
  const baseFields = extractCheckoutStartedBaseFields(event);
  const rawEvent = parseCheckoutStartedRawEvent(event);
  const checkoutInfo = parseCheckoutStartedCheckoutInfo(event);
  if ("cart" in event) {
    return {
      ...baseFields,
      eventType: event.eventType,
      storefrontProvider: event.storefrontProvider,
      rawEvent,
      checkoutInfo,
    };
  }
  if (event.eventType === CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED) {
    return {
      ...baseFields,
      eventType: CustomerEventType.CHECKOUT_STARTED,
      storefrontProvider: Provider.SHOPIFY,
      rawEvent,
      checkoutInfo,
    };
  }
  return event;
}

//////////////////////////////////
///// CHECKOUT COMPLETED ////////
//////////////////////////////////

const baseCheckoutCompletedShoppingEventSchema = baseShoppingEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.CHECKOUT_COMPLETED),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
  },
);
const shopifyCheckoutCompletedShoppingEventSchema =
  baseCheckoutCompletedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.SHOPIFY),
    checkout: shopifyCheckoutSchema.nullish(),
  });
const commentsoldCheckoutCompletedShoppingEventSchema =
  baseCheckoutCompletedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
export const CheckoutCompletedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyCheckoutCompletedShoppingEventSchema,
    commentsoldCheckoutCompletedShoppingEventSchema,
  ],
);
export type CheckoutCompletedShoppingEvent = z.infer<
  typeof CheckoutCompletedShoppingEventSchema_
>;

export function standardizeCheckoutCompletedShoppingEvent(
  event: CheckoutCompletedShoppingEvent,
): CheckoutCompletedShoppingEvent {
  if ("checkout" in event) {
    return {
      _id: event._id,
      team: event.team,
      email: event.email,
      customer: event.customer,
      timestamp: event.timestamp,
      idempotencyKey: event.idempotencyKey,
      isDuplicate: event.isDuplicate,
      eventType: event.eventType,
      importSource: event.importSource,
      aiAttributions: event.aiAttributions,
      eventSource: event.eventSource,
      externalEventId: event.externalEventId,
      externalCustomer: event.externalCustomer,
      sessionId: event.sessionId,
      ipAddress: event.ipAddress,
      storefrontProvider: event.storefrontProvider,

      rawEvent: event.rawEvent ?? { checkout: event.checkout },

      checkoutInfo:
        event.checkoutInfo ??
        (event.checkout
          ? shopifyCheckoutToRedoCheckout({
              checkout: event.checkout,
              cartId: null,
            })
          : null),
    };
  }
  return event;
}

//////////////////////////////////
///// PAYMENT FAILED ////////////
//////////////////////////////////

export const PaymentFailedShoppingEventSchema_ = baseShoppingEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.PAYMENT_FAILED),
    paymentFailureStage: createEnum(PaymentFailureStage, {
      enumName: "PaymentFailureStage",
    }),
    externalOrderId: z.string().nullish(),
  },
);
export const PaymentFailedShoppingEventSchema: z.ZodType<PaymentFailedShoppingEvent> =
  PaymentFailedShoppingEventSchema_;
export interface PaymentFailedShoppingEvent
  extends z.infer<typeof PaymentFailedShoppingEventSchema_> {}

//////////////////////////////////
///// CHECKOUT INFO CHANGED //////
//////////////////////////////////

// is a shopping event, but does not need externalEventId
export const CheckoutInfoChangedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_INFO_CHANGED),
    checkoutToken: z.string(),
    subtotalPrice: MoneySchema,
    itemQuantity: z.number(),
    customerOrderCount: z.union([
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.KNOWN),
        value: z.number(),
      }),
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.UNKNOWN),
      }),
    ]),
    treatmentId: z.union([
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.KNOWN),
        value: z.string().optional(),
      }),
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.UNKNOWN),
      }),
    ]),
    localDateTime: z.object({
      isoDateTime: z.date(),
      timeZone: z.string(),
      localDayOfWeek: createEnum(LocalDayOfWeek, {
        enumName: "LocalDayOfWeek",
      }),
      localTimeInSecondsSinceMidnight: z.number(),
    }),
    location: z
      .object({
        countryCode: z.string().optional(),
        provinceCode: z.string().optional(),
      })
      .optional(),
    metricSource: createEnum(CheckoutConversionMetricSource, {
      enumName: "CheckoutConversionMetricSource",
    }).optional(),
  });
export const CheckoutInfoChangedShoppingEventSchema: z.ZodType<CheckoutInfoChangedShoppingEvent> =
  CheckoutInfoChangedShoppingEventSchema_;
export interface CheckoutInfoChangedShoppingEvent
  extends z.infer<typeof CheckoutInfoChangedShoppingEventSchema_> {}

////////////////////////////////////////////////////
///// CHECKOUT PROBABILITY CALCULATED //////////////
////////////////////////////////////////////////////

// is a shopping event, but does not need externalEventId
export const CheckoutProbabilityCalculatedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_PROBABILITY_CALCULATED),
    sessionId: z.string(),
    instantaneousHazardProbability: z.number(),
    cumulativeConversionProbability: z.number(),
    sessionAge: z.number(),
    totalBuckets: z.number(),
  });
export const CheckoutProbabilityCalculatedShoppingEventSchema: z.ZodType<CheckoutProbabilityCalculatedShoppingEvent> =
  CheckoutProbabilityCalculatedShoppingEventSchema_;
export interface CheckoutProbabilityCalculatedShoppingEvent
  extends z.infer<typeof CheckoutProbabilityCalculatedShoppingEventSchema_> {}

////////////////////////////////////////////////////
///// PACKAGE PROTECTION PRICE EXPOSED //
////////////////////////////////////////////////////

export const PackageProtectionPriceExposedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.PACKAGE_PROTECTION_PRICE_EXPOSED),
    anonymousId: z.string(),
    treatmentId: z.string().optional(),
    isStagecoachArm: z.boolean(),
    modelVersionId: z.string().optional(),
    betaSampleIndex: z.number().optional(),
    packageProtectionPriceDollars: z.number().optional(),
    returnsCoveragePriceDollars: z.number().optional(),
    cartValue: z.number(),
    cartItemCount: z.number(),
    cartHash: z.string(),
    checkoutProbability: z.number().optional(),
  });
export const PackageProtectionPriceExposedShoppingEventSchema: z.ZodType<PackageProtectionPriceExposedShoppingEvent> =
  PackageProtectionPriceExposedShoppingEventSchema_;
export interface PackageProtectionPriceExposedShoppingEvent
  extends z.infer<typeof PackageProtectionPriceExposedShoppingEventSchema_> {}

export type PixelEvent =
  | ProductViewedShoppingEvent
  | ProductAddedToCartShoppingEvent
  | ProductRemovedFromCartShoppingEvent
  | CheckoutContactInfoSubmittedShoppingEvent
  | CheckoutStartedShoppingEvent
  | CheckoutCompletedShoppingEvent
  | PaymentFailedShoppingEvent
  | StorePageViewedShoppingEvent
  | CollectionViewedShoppingEvent
  | CheckoutInfoChangedShoppingEvent
  | CheckoutProbabilityCalculatedShoppingEvent
  | PackageProtectionPriceExposedShoppingEvent;
